import { fetcher } from 'api/client';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { fetchContract } from 'api/actions/contracts';

import { useEditorState } from '../../editor-context'
import { useParams } from 'react-router-dom';
import createPersistedState from 'use-persisted-state';

const useAuthPersistedState = createPersistedState('auth');

const WordFormatWrapper = styled.div`
    height: calc(100vh - 40px);
`
const langRegion = (language) => {
    switch (language) {
        case 'en': return 'en-us';
        case 'pt': return 'pt-br';
        case 'de': return 'de-de';
        case 'es': default: return 'es-mx';
    }
}

export function WordFormatElement({ wordonline = {}, previewUrl }) {
    const { i18n } = useTranslation();

    const src = useMemo(() => {
        const currentVersion = wordonline.versions?.find(v => v.versionId === wordonline.Version)
        if (!currentVersion) return '';
        const url = previewUrl || currentVersion.url;
        const uilang = langRegion(i18n.language);

        return wordonline.key.split('.').pop() === 'docx' ?
            `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURI(url)}&ui=${uilang}&rs=${uilang}&amp;wdStartOn=1&amp;wdPrint=0&amp;wdEmbedCode=0`
            :
            `https://drive.google.com/viewerng/viewer?url=${encodeURI(url)}&embedded=true&test=1`;

    }, [wordonline, i18n.language, previewUrl])


    return (

        <WordFormatWrapper className="editor-canvas-word w-100" id="document-container">
            <iframe
                src={src}
                name="office_frame"
                id="office_frame"
                className="office_frame w-100"
                title="Office Online Frame"
                height="100%" width="100%"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox"
                allowFullScreen
                seamless
                frameBorder="0"
            />
        </WordFormatWrapper>
    )
}

export function WordFormat() {

    const { document: doc } = useEditorState()

    return <WordFormatElement wordonline={doc?.wordonline} />
}

export function WordOnline() {

    const { contractId } = useParams();

    const formRef = useRef();

    const frameHolder = useRef();

    const [auth] = useAuthPersistedState({});
    const { i18n } = useTranslation();

    const { data: contract = {} } = useQuery(['wordonline-document'], () => fetcher(fetchContract(contractId)), {
        enabled: !!contractId, 
        refetchOnWindowFocus: false,
        onSuccess: (contract = {}) => {
            if (contract.wordonline?.key && contract._id) {

                const office_frame = document.createElement('iframe');
                office_frame.name = 'office_frame';
                office_frame.id = 'office_frame';
                office_frame.title = 'Office Frame';
                office_frame.setAttribute('allowfullscreen', 'true');
    
                frameHolder.current.appendChild(office_frame);
    
                const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
                link.type = 'image/x-icon';
                link.rel = 'shortcut icon';
    
                document.title = `${document.title} - ${contract.wordonline.BaseFileName}`;
    
                let url = null;
    
                switch (contract.wordonline.key.split('.').pop().toLowerCase()) {
                    case 'docx': case 'doc': default:
                        url = 'https://word-edit.officeapps.live.com/we/wordeditorframe.aspx?new=1&IsLicensedUser=1&';
                        link.href = 'https://word-view.officeapps.live.com/wv/resources/1033/FavIcon_Word.ico';
                        break;
                    case 'xlsx':
                        url = 'https://excel.officeapps.live.com/x/_layouts/xlviewerinternal.aspx?edit=1&new=1&IsLicensedUser=1&';
                        link.href = 'https://excel.officeapps.live.com/x/_layouts/resources/FavIcon_Excel.ico';
                        break;
                    case 'pptx':
                        url = 'https://powerpoint.officeapps.live.com/p/PowerPointFrame.aspx?PowerPointView=EditView&New=1&IsLicensedUser=1&';
                        link.href = 'https://powerpoint.officeapps.live.com/p/resources/1033/FavIcon_Ppt.ico';
                        break;
                }
    
                document.getElementsByTagName('head')[0].appendChild(link);
    
                const uiLang = langRegion(i18n.language);
    
                const wopiUrl = encodeURIComponent('https://wopi.api.trato.io/wopi/files/')
    
                const wopiAction = `${url}ui=${uiLang}&rs=${uiLang}&WOPISrc=${wopiUrl}${contract._id}`;
    
                formRef.current.setAttribute('action', wopiAction);
    
                formRef.current.submit();
            }
    
        }
    })


    return (
        <>
            <form id="office_form" name="office_form" target="office_frame" ref={formRef} method="post" >
                <input name="access_token" value={`${process.env.REACT_APP_MAIN_DOMAIN}|${auth.accessToken}`} type="hidden" />
                <input name="access_token_ttl" value={auth.expiresAt} type="hidden" />
            </form >

            <span id="frameholder" className="frameholder" ref={frameHolder}></span>
        </>
    )
}